import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { MicrosoftTeamsCallQueue, MicrosoftTeamsCallQueueView } from '../shared/models/generated/ziroModels';
import { MicrosoftTenantsStateContext } from '../shared/contexts/microsoft-tenants-state.context';
import { ToastService } from '../shared/services/toast.service';
import { AuthenticationContext } from '../shared/contexts/authentication.context';

@Injectable()
export class TeamsCallQueuesContext implements OnDestroy {
  private _http = inject(HttpClient);
  private _tenantsStateContext = inject(MicrosoftTenantsStateContext);
  private _toastService = inject(ToastService);
  private _authenticationContext = inject(AuthenticationContext);

  private _stateSource = new ReplaySubject<MicrosoftTeamsCallQueue[]>(1);
  state$ = this._stateSource.asObservable();

  private _baseUrl = '/services/microsoft/macs/tenants';
  private _tenantId: string | null = null;

  private _subscriptions = new Subscription();
  constructor() {
    const sub = this._authenticationContext.state$
      .pipe(
        take(1),
        switchMap(() =>
          this._tenantsStateContext.cachedTenantState$.pipe(
            filter((tenant) => !!tenant?.tenantId),
            take(1),
          ),
        ),
        switchMap((tenant) => {
          this._tenantId = tenant.tenantId.toString();
          return this._loadAllCallQueues();
        }),
      )
      .subscribe((queues) => this._stateSource.next(queues));

    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  refreshAllData(): Observable<void> {
    return this._loadAllCallQueues().pipe(
      tap((queues: MicrosoftTeamsCallQueue[]): void => this._stateSource.next(queues)),
      map((): null => null),
    );
  }

  deleteCallQueue(callQueueId: string, name?: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${this._tenantId}/call-queues/${callQueueId}`).pipe(
      switchMap(() => this.refreshAllData()),
      tap(() => {
        if (name) {
          this._toastService.pushDeleteToast('tkey;teams_call_queues.list_view.toast.service_type', name);
        }
      }),
    );
  }

  createCallQueue(callQueue: MicrosoftTeamsCallQueue): Observable<void> {
    return this._http
      .post<void>(`/services/microsoft/macs/tenants/${this._tenantId}/call-queues`, callQueue)
      .pipe(switchMap(() => this.refreshAllData()));
  }

  updateCallQueue(callQueue: MicrosoftTeamsCallQueue): Observable<void> {
    return this._http
      .put<void>(`/services/microsoft/macs/tenants/${this._tenantId}/call-queues/${callQueue.callQueueId}`, callQueue)
      .pipe(switchMap(() => this.refreshAllData()));
  }

  getCallQueue(callQueueId: string, tenantId: string): Observable<MicrosoftTeamsCallQueueView> {
    return this._http.get<MicrosoftTeamsCallQueueView>(
      `/services/microsoft/views/tenants/${tenantId}/teams-call-queues-details/${callQueueId}`,
    );
  }

  private _loadAllCallQueues(): Observable<MicrosoftTeamsCallQueue[]> {
    return this._http.get<MicrosoftTeamsCallQueue[]>(`${this._baseUrl}/${this._tenantId}/call-queues`);
  }
}
